import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { CashSaleService } from '../../services/cashSale.service';
import { CustomerService } from '../../services/customer.service';
import { SalesInvoice, SALES_INVOICE_STATUS_COLORS } from '../../interfaces/sales/salesInvoice.interface';
import { Customer } from '../../interfaces/sales/customer.interface';
import SearchableSelect from '../common/SearchableSelect';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';

const CashSaleList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canManageInvoices = usePermission(PERMISSIONS.SALES_INVOICE_MANAGE);
  const canManageReceipts = usePermission(PERMISSIONS.RECEIPT_MANAGE);
  const canManage = canManageInvoices && canManageReceipts;
  const [rows, setRows] = useState<SalesInvoice[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState({ from: '', to: '', customerId: '', search: '' });   // filters sent to the server

  useEffect(() => {
    // customers of any status: an inactive walk-in customer's old sales must stay filterable
    CustomerService.listByInstitution(AuthService.getSelectedInstitution(), 0, 200, 'ALL')
      .then(p => setCustomers(p.content)).catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    const institutionId = AuthService.getSelectedInstitution();
    setLoading(true);
    CashSaleService.list(institutionId, {
      from: applied.from || undefined, to: applied.to || undefined,
      customerId: applied.customerId || undefined, search: applied.search || undefined, page, size,
    })
      .then(res => { setRows(res.content); setTotal(res.totalElements); })
      .catch(e => toast.showApiError(e, 'Failed to load cash sales'))
      .finally(() => setLoading(false));
  }, [page, size, applied]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = () => { setPage(0); setApplied({ from, to, customerId, search }); };

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.cashSaleList.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Cash Sales</Typography>
        {canManage && (
          <Button data-help="sales.cashSaleList.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/sales/cash-sales/new')}>New Cash Sale</Button>
        )}
      </Box>
      <Paper sx={{ p: 2, mb: 2 }} data-help-section="sales.cashSaleList.filters">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField data-help="sales.cashSaleList.from" label="From" type="date" size="small" value={from} onChange={e => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }} />
          <TextField data-help="sales.cashSaleList.to" label="To" type="date" size="small" value={to} onChange={e => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }} />
          <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
            <SearchableSelect data-help="sales.cashSaleList.customerFilter" label="Customer" size="small" options={customers} value={customerId}
              onChange={id => setCustomerId(id)} getOptionLabel={c => c.name} clearable />
          </Box>
          <TextField data-help="sales.cashSaleList.search" label="Search (number, customer, buyer)" size="small" value={search}
            onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') apply(); }}
            sx={{ flex: '1 1 220px', maxWidth: 320 }} />
          <Button data-help="sales.cashSaleList.apply" variant="outlined" onClick={apply}>Apply</Button>
        </Box>
      </Paper>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} data-help-section="sales.cashSaleList.table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-help="sales.cashSaleList.col.number">Invoice #</TableCell>
                <TableCell data-help="sales.cashSaleList.col.date">Date</TableCell>
                <TableCell data-help="sales.cashSaleList.col.customer">Customer</TableCell>
                <TableCell data-help="sales.cashSaleList.col.buyer">Buyer</TableCell>
                <TableCell align="right" data-help="sales.cashSaleList.col.total">Total</TableCell>
                <TableCell data-help="sales.cashSaleList.col.status">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/sales/invoices/${row.id}`)}>
                  <TableCell>{row.invoiceNumber ?? '-'}</TableCell>
                  <TableCell>{formatDate(row.issueDate)}</TableCell>
                  <TableCell>{row.customerName ?? '-'}</TableCell>
                  <TableCell>{row.walkInName ?? '-'}</TableCell>
                  <TableCell align="right">{formatMoney(row.totalAmount)}</TableCell>
                  <TableCell><Chip data-help="sales.cashSaleList.status" size="small" label={humanizeEnum(row.status)} color={SALES_INVOICE_STATUS_COLORS[row.status]} /></TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={6}><Typography color="text.secondary">No cash sales found.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size} onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }} />
        </TableContainer>
      )}
    </Box>
  );
};

export default CashSaleList;
